import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plus, Edit2, Trash2, X, Save, Search, Users, DollarSign, AlertTriangle, CircleDollarSign, Check, Archive } from "lucide-react";
import KrakenLoader from "@/components/KrakenLoader";

const CATEGORIES = [
  ["team_dues", "Team Dues"], ["uniform_fees", "Uniform Fees"], ["tournament_fees", "Tournament Fees"],
  ["camps", "Camp Fees"], ["sponsorships", "Sponsorship"], ["donations", "Donation"],
  ["merch", "Merchandise"], ["other", "Other"],
];
const STATUSES = [
  ["unpaid", "Unpaid"], ["paid", "Paid"], ["partial", "Partial"], ["overdue", "Overdue"], ["waived", "Waived"],
];
const METHODS = [["", "—"], ["stripe", "Stripe"], ["cash", "Cash"], ["check", "Check"], ["offline", "Offline"], ["waived", "Waived"]];
const CAT_LABEL = Object.fromEntries(CATEGORIES);
const STATUS_COLOR = {
  paid: "bg-green-500/15 text-green-300 border-green-400/40",
  partial: "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  unpaid: "bg-slate-500/15 text-slate-400 border-slate-400/30",
  overdue: "bg-red-500/15 text-red-300 border-red-400/40",
  waived: "bg-blue-500/15 text-blue-300 border-blue-400/40",
};
const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 inline-block ${STATUS_COLOR[status] || ""}`}>
      {status}
    </span>
  );
}

function SummaryCard({ label, value, hint, accent, testId }) {
  return (
    <div className={`kr-card rounded-xl p-4 sm:p-5 ${accent || ""}`} data-testid={testId}>
      <div className="eyebrow text-[10px] mb-1">{label}</div>
      <div className="font-display text-2xl sm:text-3xl tracking-tight">{value}</div>
      {hint ? <div className="text-[11px] text-slate-400 mt-1">{hint}</div> : null}
    </div>
  );
}

const blankEdit = {
  title: "", description: "", category: "team_dues", amount: 0, due_date: "",
  status: "unpaid", amount_paid: 0, payment_date: "", payment_method: "",
  admin_notes: "", player_name: "",
};

export default function AdminPaymentsHub() {
  const [accounts, setAccounts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total_assigned: 0, total_paid: 0, total_outstanding: 0, overdue_amount: 0, open_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [partialFor, setPartialFor] = useState(null); // payment row → prompt for amount
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, s] = await Promise.all([
        api.get("/admin/accounts"),
        api.get("/admin/payments"),
        api.get("/admin/payments/summary"),
      ]);
      setAccounts(a.data);
      setPayments(p.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const accountById = useMemo(() => {
    const m = {};
    accounts.forEach((a) => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return payments.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (!n) return true;
      const acc = accountById[p.user_id] || {};
      const hay = [p.title, p.description, p.player_name, acc.name, acc.player_name, acc.email].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(n);
    });
  }, [payments, q, statusFilter, categoryFilter, accountById]);

  const setStatus = async (row, newStatus, extras = {}) => {
    const patch = { status: newStatus, ...extras };
    if (newStatus === "paid") {
      patch.amount_paid = Number(row.amount) || 0;
      patch.payment_date = new Date().toISOString().slice(0, 10);
    } else if (newStatus === "unpaid") {
      patch.amount_paid = 0;
      patch.payment_date = "";
    } else if (newStatus === "waived") {
      patch.amount_paid = 0;
      patch.payment_date = new Date().toISOString().slice(0, 10);
    }
    try {
      await api.put(`/admin/payments/${row.id}`, patch);
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const submitPartial = async () => {
    const row = partialFor;
    const paid = Number(partialFor.amount_paid);
    if (!Number.isFinite(paid) || paid < 0) { toast.error("Enter a valid amount"); return; }
    const amount = Number(row.amount) || 0;
    const status = paid >= amount && amount > 0 ? "paid" : (paid > 0 ? "partial" : "unpaid");
    try {
      await api.put(`/admin/payments/${row.id}`, {
        amount_paid: paid, status,
        payment_date: paid > 0 ? new Date().toISOString().slice(0, 10) : "",
      });
      toast.success("Updated");
      setPartialFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const e0 = editing;
    try {
      const payload = {
        title: e0.title, description: e0.description, category: e0.category,
        amount: Number(e0.amount) || 0, due_date: e0.due_date || "",
        status: e0.status, amount_paid: Number(e0.amount_paid) || 0,
        payment_date: e0.payment_date || "", payment_method: e0.payment_method || "",
        admin_notes: e0.admin_notes || "", player_name: e0.player_name || "",
      };
      await api.put(`/admin/payments/${e0.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/payments/${confirmDelete.id}`);
      toast.success("Payment deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  if (loading) return <KrakenLoader />;

  return (
    <div data-testid="admin-payments-hub" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-[#00E5FF] inline-flex items-center gap-1">
            <ChevronLeft size={14} /> Admin
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl uppercase mt-1">Payments</h1>
          <p className="text-slate-400 text-sm">Manage dues, fees, donations, sponsorships, and merch payments across all parents.</p>
        </div>
        <button onClick={() => setCreatingOpen(true)} className="btn-primary" data-testid="add-payment-btn">
          <Plus size={14} /> Add Payment
        </button>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <SummaryCard label="Total Assigned"     value={fmtMoney(summary.total_assigned)}      hint={`${summary.total_count} records`} testId="sum-assigned" />
        <SummaryCard label="Total Paid"         value={fmtMoney(summary.total_paid)}                                                  testId="sum-paid" />
        <SummaryCard label="Total Outstanding"  value={fmtMoney(summary.total_outstanding)}                                           testId="sum-outstanding" />
        <SummaryCard label="Overdue Amount"     value={fmtMoney(summary.overdue_amount)}      hint="Past due, unpaid"                 testId="sum-overdue" />
        <SummaryCard label="Open Requests"      value={summary.open_count}                    hint="Unpaid / Partial / Overdue"       testId="sum-open" />
      </div>

      {/* FILTERS */}
      <div className="kr-card rounded-xl p-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, parent, player, email…"
            className="w-full bg-[#050A15] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm"
            data-testid="payments-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="filter-status">
          <option value="all">All statuses</option>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="filter-category">
          <option value="all">All categories</option>
          {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* PAYMENTS TABLE / CARDS */}
      {filtered.length === 0 ? (
        <div className="kr-card rounded-xl p-10 text-center text-slate-400" data-testid="payments-empty">
          <CircleDollarSign size={28} className="mx-auto text-slate-600 mb-2" />
          {payments.length === 0
            ? "No payment requests yet. Click \"Add Payment\" to create one."
            : "No payments match your filters."}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block kr-card rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#050A15] text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left p-3">Payment</th>
                  <th className="text-left p-3">Parent / Player</th>
                  <th className="text-right p-3">Due</th>
                  <th className="text-right p-3">Paid</th>
                  <th className="text-right p-3">Balance</th>
                  <th className="text-left p-3">Due Date</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const acc = accountById[p.user_id] || {};
                  return (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`payment-row-${p.id}`}>
                      <td className="p-3">
                        <div className="font-semibold">{p.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]/80">{CAT_LABEL[p.category] || p.category}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-slate-200">{acc.name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{p.player_name || acc.player_name || "—"} · {acc.email || "—"}</div>
                      </td>
                      <td className="p-3 text-right">{fmtMoney(p.amount)}</td>
                      <td className="p-3 text-right">{fmtMoney(p.amount_paid)}</td>
                      <td className="p-3 text-right font-semibold">{fmtMoney(p.remaining_balance)}</td>
                      <td className="p-3">{fmtDate(p.due_date)}</td>
                      <td className="p-3"><StatusBadge status={p.status} /></td>
                      <td className="p-3">
                        <RowActions row={p} onEdit={() => setEditing({ ...p })} onDelete={() => setConfirmDelete(p)} onStatus={(s) => setStatus(p, s)} onPartial={() => setPartialFor({ ...p, amount_paid: p.amount_paid || 0 })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {filtered.map((p) => {
              const acc = accountById[p.user_id] || {};
              return (
                <div key={p.id} className="kr-card rounded-xl p-4" data-testid={`payment-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{p.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]/80">{CAT_LABEL[p.category] || p.category}</div>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="text-sm text-slate-300 truncate">{acc.name || "—"} · <span className="text-slate-500">{acc.email || "—"}</span></div>
                  <div className="text-xs text-slate-400 mt-1">Player: {p.player_name || acc.player_name || "—"}</div>
                  <div className="grid grid-cols-3 text-center gap-1 mt-3 text-xs">
                    <div className="bg-[#050A15]/60 rounded p-2"><div className="text-[10px] text-slate-500 uppercase">Due</div><div className="font-semibold">{fmtMoney(p.amount)}</div></div>
                    <div className="bg-[#050A15]/60 rounded p-2"><div className="text-[10px] text-slate-500 uppercase">Paid</div><div className="font-semibold">{fmtMoney(p.amount_paid)}</div></div>
                    <div className="bg-[#050A15]/60 rounded p-2"><div className="text-[10px] text-slate-500 uppercase">Bal</div><div className="font-semibold">{fmtMoney(p.remaining_balance)}</div></div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">Due {fmtDate(p.due_date)}</div>
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <RowActions row={p} mobile onEdit={() => setEditing({ ...p })} onDelete={() => setConfirmDelete(p)} onStatus={(s) => setStatus(p, s)} onPartial={() => setPartialFor({ ...p, amount_paid: p.amount_paid || 0 })} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* CREATE MODAL */}
      {creatingOpen && (
        <CreatePaymentModal
          accounts={accounts}
          onClose={() => setCreatingOpen(false)}
          onCreated={() => { setCreatingOpen(false); load(); }}
        />
      )}

      {/* EDIT MODAL */}
      {editing && (
        <EditPaymentModal
          editing={editing}
          setEditing={setEditing}
          accountById={accountById}
          onSave={saveEdit}
        />
      )}

      {/* PARTIAL AMOUNT MODAL */}
      {partialFor && (
        <PartialModal
          partialFor={partialFor}
          setPartialFor={setPartialFor}
          onSubmit={submitPartial}
        />
      )}

      {/* DELETE CONFIRMATION */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="kr-card rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-modal">
            <AlertTriangle className="text-red-400 mb-2" size={24} />
            <h3 className="font-display text-xl uppercase mb-2">Delete this payment?</h3>
            <p className="text-sm text-slate-300 mb-4">
            &quot;{confirmDelete.title}&quot; — {fmtMoney(confirmDelete.amount)}. <br />
              This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost flex-1 justify-center" data-testid="delete-cancel">Cancel</button>
              <button onClick={doDelete} className="flex-1 justify-center inline-flex items-center gap-1 bg-red-500/20 border border-red-400/50 text-red-200 hover:bg-red-500/30 rounded-md px-3 py-1.5 text-sm" data-testid="delete-confirm">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RowActions({ row, mobile, onEdit, onDelete, onStatus, onPartial }) {
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${mobile ? "justify-start" : "justify-end"}`}>
      {row.status !== "paid" && <button onClick={() => onStatus("paid")} className="text-[11px] px-2 py-1 rounded bg-green-500/15 text-green-300 border border-green-400/30 hover:bg-green-500/25" data-testid={`mark-paid-${row.id}`}>Mark Paid</button>}
      {row.status !== "partial" && <button onClick={onPartial} className="text-[11px] px-2 py-1 rounded bg-yellow-500/15 text-yellow-300 border border-yellow-400/30 hover:bg-yellow-500/25" data-testid={`mark-partial-${row.id}`}>Partial</button>}
      {row.status !== "unpaid" && <button onClick={() => onStatus("unpaid")} className="text-[11px] px-2 py-1 rounded bg-slate-500/15 text-slate-300 border border-slate-400/30 hover:bg-slate-500/25" data-testid={`mark-unpaid-${row.id}`}>Unpaid</button>}
      {row.status !== "waived" && <button onClick={() => onStatus("waived")} className="text-[11px] px-2 py-1 rounded bg-blue-500/15 text-blue-300 border border-blue-400/30 hover:bg-blue-500/25" data-testid={`mark-waived-${row.id}`}>Waive</button>}
      <button onClick={onEdit} className="text-slate-300 hover:text-[#00E5FF] p-1.5" title="Edit" data-testid={`edit-${row.id}`}><Edit2 size={14} /></button>
      <button onClick={onDelete} className="text-slate-300 hover:text-red-300 p-1.5" title="Delete" data-testid={`del-${row.id}`}><Trash2 size={14} /></button>
    </div>
  );
}

function CreatePaymentModal({ accounts, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", category: "team_dues",
    amount: "", due_date: "", admin_notes: "",
  });
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const eligible = useMemo(
    () => accounts.filter((a) => ["parent", "player"].includes(a.role) && a.account_status !== "disabled"),
    [accounts],
  );
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return eligible;
    return eligible.filter((a) => [a.name, a.player_name, a.email].filter(Boolean).join(" ").toLowerCase().includes(n));
  }, [eligible, q]);

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      const next = new Set(selected);
      filtered.forEach((a) => next.delete(a.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filtered.forEach((a) => next.add(a.id));
      setSelected(next);
    }
  };
  const clearSel = () => setSelected(new Set());
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Valid amount required"); return; }
    if (selected.size === 0) { toast.error("Select at least one parent"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/payments/bulk", {
        user_ids: Array.from(selected),
        title: form.title.trim(),
        description: form.description, category: form.category,
        amount: amt, due_date: form.due_date,
        admin_notes: form.admin_notes,
      });
      toast.success(`Created ${r.data.created} payment${r.data.created === 1 ? "" : "s"}`);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="create-payment-modal">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="kr-card rounded-2xl p-6 sm:p-8 max-w-3xl w-full my-8 space-y-4 relative">
          <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" aria-label="Close"><X size={20} /></button>
          <div className="eyebrow">New payment request</div>
          <h2 className="font-display text-2xl uppercase">Add Payment</h2>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Title *</span>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Spring Team Dues" className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-title" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-category">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Amount Due *</span>
              <input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-amount" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Due Date</span>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-due" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Description (visible to parents)</span>
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-desc" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Internal Admin Notes (not visible to parents)</span>
              <textarea rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="new-pay-notes" />
            </label>
          </div>

          {/* Multi-select parents */}
          <div className="bg-[#050A15]/40 border border-white/10 rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-[#00E5FF]" />
                <div className="text-sm font-semibold">Assign to Parents</div>
                <span className="text-xs text-slate-400">{selected.size} selected</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={toggleAll} className="text-[11px] px-2 py-1 rounded bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/20" data-testid="select-all-parents">
                  {filtered.length > 0 && filtered.every((a) => selected.has(a.id)) ? "Deselect All (filtered)" : "Select All Active Parents"}
                </button>
                <button type="button" onClick={clearSel} className="text-[11px] px-2 py-1 rounded text-slate-300 hover:text-white border border-white/10" data-testid="clear-parents">Clear</button>
              </div>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parents…" className="w-full bg-[#050A15] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm" data-testid="parent-search" />
            </div>
            <div className="max-h-60 overflow-y-auto rounded-md border border-white/10 divide-y divide-white/5">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 text-center">No parents match</div>
              ) : filtered.map((a) => (
                <label key={a.id} className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-white/[0.03] ${selected.has(a.id) ? "bg-[#00E5FF]/5" : ""}`} data-testid={`parent-opt-${a.id}`}>
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} className="accent-[#00E5FF]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{a.name || "(no name)"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{a.player_name || "—"} · {a.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary flex-1 justify-center disabled:opacity-50" data-testid="submit-new-payment">
              <Plus size={14} /> Create Payment{selected.size > 1 ? "s" : ""} ({selected.size})
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPaymentModal({ editing, setEditing, accountById, onSave }) {
  const acc = accountById[editing.user_id] || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={() => setEditing(null)} data-testid="edit-payment-modal">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <form onClick={(e) => e.stopPropagation()} onSubmit={onSave} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 space-y-3 relative">
          <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white" aria-label="Close"><X size={20} /></button>
          <div className="eyebrow">Edit payment</div>
          <h2 className="font-display text-2xl uppercase">{editing.title}</h2>
          <div className="text-xs text-slate-400">Assigned to: <span className="text-slate-200">{acc.name || "—"}</span> · {acc.email || "—"}</div>
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Title</span>
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Category</span>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Amount Due</span>
              <input type="number" min="0" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Amount Paid</span>
              <input type="number" min="0" step="0.01" value={editing.amount_paid} onChange={(e) => setEditing({ ...editing, amount_paid: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Due Date</span>
              <input type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Payment Date</span>
              <input type="date" value={editing.payment_date || ""} onChange={(e) => setEditing({ ...editing, payment_date: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Status</span>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm">
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Method</span>
              <select value={editing.payment_method || ""} onChange={(e) => setEditing({ ...editing, payment_method: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm">
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Description</span>
              <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Admin Notes</span>
              <textarea rows={2} value={editing.admin_notes || ""} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })} className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button type="submit" className="btn-primary flex-1 justify-center" data-testid="save-edit-payment"><Save size={14} /> Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PartialModal({ partialFor, setPartialFor, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPartialFor(null)}>
      <div className="kr-card rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="partial-modal">
        <div className="eyebrow">Record partial payment</div>
        <h3 className="font-display text-xl uppercase mb-3">{partialFor.title}</h3>
        <div className="text-xs text-slate-400 mb-1">Amount due: {fmtMoney(partialFor.amount)}</div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Amount paid</span>
          <input
            type="number" min="0" step="0.01"
            value={partialFor.amount_paid}
            onChange={(e) => setPartialFor({ ...partialFor, amount_paid: e.target.value })}
            className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm"
            data-testid="partial-amount"
            autoFocus
          />
        </label>
        <p className="text-[11px] text-slate-500 mt-2">
          If the new total equals the amount due, this will be marked Paid. Otherwise it stays Partial.
        </p>
        <div className="flex gap-2 mt-4">
          <button onClick={() => setPartialFor(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
          <button onClick={onSubmit} className="btn-primary flex-1 justify-center" data-testid="partial-submit"><Check size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}
